import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { updateDevice, updateDeviceAccumulators, sendCommand, fetchDevicePosition, type Device, type Group } from '@/lib/traccar-api';
import { VEHICLE_BRANDS, CATEGORY_TO_VEHICLE_TYPE, get3DIconUrl, ROTATION_FRAMES, DEVICE_CATEGORIES } from '@/data/car-brands';

interface EditDeviceDialogProps {
  device: Device;
  groups: Group[];
  trigger?: React.ReactNode;
  open?: boolean;
  onClose?: () => void;
}

export function EditDeviceDialog({ device, groups, trigger, open: openProp, onClose }: EditDeviceDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(openProp ?? false);

  useEffect(() => {
    if (openProp !== undefined) setOpen(openProp);
  }, [openProp]);

  useEffect(() => {
    if (open) setFormData(buildFormData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device.id, open]);

  const { data: currentPosition } = useQuery({
    queryKey: ['device-position', device.id],
    queryFn: () => fetchDevicePosition(device.id),
    enabled: open,
    staleTime: 0,
  });

  const currentOdometerKm = currentPosition?.attributes?.totalDistance != null
    ? ((currentPosition.attributes.totalDistance as number) / 1000).toFixed(0)
    : null;
  const currentEngineHours = currentPosition?.attributes?.hours != null
    ? ((currentPosition.attributes.hours as number) / 3600000).toFixed(1)
    : null;

  const [makeOpen, setMakeOpen] = useState(false);
  const [modelOpen, setModelOpen] = useState(false);
  const [makeSearch, setMakeSearch] = useState('');
  const [modelSearch, setModelSearch] = useState('');
  const [rotationFrame, setRotationFrame] = useState(0);

  useEffect(() => {
    if (!open) return;
    const interval = setInterval(
        () => setRotationFrame((rotationFrame) => (rotationFrame + ROTATION_FRAMES) % 360),
        500
    );
    return () => clearInterval(interval);
  }, [open]);
  const buildFormData = () => ({
    name: device.name,
    groupId: device.groupId,
    category: device.category || '',
    make: String(device.attributes?.make || ''),
    model: String(device.attributes?.vehicleModel || ''),
    year: String(device.attributes?.year || ''),
    modelYear: String(device.attributes?.modelYear || ''),
    color: String(device.attributes?.color || ''),
    licensePlate: String(device.attributes?.licensePlate || ''),
    vin: String(device.attributes?.vin || ''),
    renavan: String(device.attributes?.renavan || ''),
    activationDate: String(device.attributes?.activationDate || ''),
    speedLimit: device.attributes?.speedLimit != null ? String(device.attributes.speedLimit) : '',
    notes: String(device.attributes?.notes || ''),
    disabled: device.disabled ?? false,
    odometerKm: '',
    engineHours: '',
  });

  const [formData, setFormData] = useState(buildFormData);

  const vehicleType = CATEGORY_TO_VEHICLE_TYPE[formData.category] ?? null;

  const filteredBrands = useMemo(
    () => vehicleType
      ? VEHICLE_BRANDS.filter((b) => b.type === vehicleType)
      : VEHICLE_BRANDS,
    [vehicleType],
  );

  const brandNames = useMemo(() => filteredBrands.map((b) => b.brand), [filteredBrands]);

  const modelsForBrand = useMemo(() => {
    const entry = VEHICLE_BRANDS.find(
      (b) => b.brand.toLowerCase() === formData.make.toLowerCase(),
    );
    return entry?.models ?? [];
  }, [formData.make]);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) onClose?.();
    if (isOpen) setFormData(buildFormData());
  };

  const updateMutation = useMutation({
    mutationFn: updateDevice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      toast.success(t('devices.editSuccess'));
      handleOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(error.message || t('devices.editError'));
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error(t('devices.editError'));
      return;
    }

    if (formData.licensePlate) {
      const existing = queryClient.getQueryData<Device[]>(['devices']) ?? [];
      const plate = formData.licensePlate.trim().toLowerCase();
      const duplicate = existing.find(
        (d) => d.id !== device.id &&
          String(d.attributes?.licensePlate ?? '').trim().toLowerCase() === plate,
      );
      if (duplicate) {
        toast.error(t('devices.licensePlateDuplicate'));
        return;
      }
    }

    const attributes: Record<string, string | number | boolean> = {
      ...(device.attributes || {}),
    };
    if (formData.make) attributes.make = formData.make;
    else delete attributes.make;
    if (formData.model) attributes.vehicleModel = formData.model;
    else delete attributes.vehicleModel;
    if (formData.year) attributes.year = formData.year;
    else delete attributes.year;
    if (formData.modelYear) attributes.modelYear = formData.modelYear;
    else delete attributes.modelYear;
    if (formData.color) attributes.color = formData.color;
    else delete attributes.color;
    if (formData.licensePlate) attributes.licensePlate = formData.licensePlate;
    else delete attributes.licensePlate;
    if (formData.vin) attributes.vin = formData.vin;
    else delete attributes.vin;
    if (formData.renavan) attributes.renavan = formData.renavan;
    else delete attributes.renavan;
    if (formData.activationDate) attributes.activationDate = formData.activationDate;
    else delete attributes.activationDate;
    if (formData.speedLimit) attributes.speedLimit = Number(formData.speedLimit);
    else delete attributes.speedLimit;
    if (formData.notes) attributes.notes = formData.notes;
    else delete attributes.notes;

    if (formData.odometerKm || formData.engineHours) {
      const totalDistance = formData.odometerKm
        ? Math.max(0, parseFloat(formData.odometerKm)) * 1000
        : currentOdometerKm ? parseFloat(currentOdometerKm) * 1000 : 0;
      const hoursMs = formData.engineHours
        ? Math.max(0, parseFloat(formData.engineHours)) * 3600000
        : currentEngineHours ? parseFloat(currentEngineHours) * 3600000 : 0;
      try {
        await updateDeviceAccumulators(device.id, totalDistance, hoursMs);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : t('devices.editError'));
        return;
      }
      if (device.model !== 'VL100') {
        sendCommand({ deviceId: device.id, type: 'custom', attributes: { data: `MILEAGE,ON,${totalDistance}#` } })
            .catch(() => {});
      } else {
        sendCommand({ deviceId: device.id, type: 'custom', attributes: { data: `GT06METER,1#` } })
            .catch(() => {});
        sendCommand({ deviceId: device.id, type: 'custom', attributes: { data: `MILEAGE,${Math.round(totalDistance/1000)}#` } })
            .catch(() => {});
      }

      sendCommand({ deviceId: device.id, type: 'custom', attributes: { data: `ACCHOUR,${hoursMs/1000/60}#` } })
          .catch(() => {});
    }

    updateMutation.mutate({
      ...device,
      name: formData.name,
      groupId: formData.groupId,
      category: formData.category,
      disabled: formData.disabled,
      attributes,
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {openProp === undefined && (
        <DialogTrigger asChild>
          {trigger || (
            <Button variant="ghost" size="sm" className="gap-1">
              <Pencil className="h-4 w-4" />
              <span className="hidden sm:inline">{t('devices.editVehicle')}</span>
            </Button>
          )}
        </DialogTrigger>
      )}
      {open && <DialogContent className="sm:max-w-[700px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{t('devices.editVehicle')}</DialogTitle>
            <DialogDescription>
              {t('devices.editVehicleDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 max-h-[80vh] overflow-y-auto p-2">
            <div className="grid gap-2">
              <Label htmlFor="edit-device-name">{t('devices.name')}</Label>
              <Input
                id="edit-device-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('devices.namePlaceholder')}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-device-group">{t('devices.group')}</Label>
                <Select
                  value={formData.groupId ? String(formData.groupId) : 'none'}
                  onValueChange={(value) =>
                    setFormData({ ...formData, groupId: value === 'none' ? 0 : Number(value) })
                  }
                >
                  <SelectTrigger id="edit-device-group">
                    <SelectValue placeholder={t('devices.selectGroup')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('devices.noGroup')}</SelectItem>
                    {groups.map((g) => (
                      <SelectItem key={g.id} value={String(g.id)}>
                        {g.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
              <Label htmlFor="edit-device-category">{t('devices.category')}</Label>
              <Select
                value={formData.category || 'default'}
                onValueChange={(value) => {
                  setFormData({ ...formData, category: value });
                }}
              >
                <SelectTrigger id="edit-device-category">
                  <div className="flex items-center gap-2">
                    <img src={get3DIconUrl(formData.category, rotationFrame, '#22c55e')} alt="" className="h-12 w-12 object-contain"/>
                    <span>{t(`devices.category_${formData.category || 'default'}`)}</span>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {[...DEVICE_CATEGORIES]
                    .sort((a, b) =>
                      t(`devices.category_${a}`).localeCompare(t(`devices.category_${b}`))
                    )
                    .map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      <div className="items-center gap-2">
                        <img src={get3DIconUrl(cat, 45, '#22c55e')} alt="" />
                        <span></span>
                        {t(`devices.category_${cat}`)}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              </div>
            </div>

            <div className="border-t pt-4 mt-1">
              <p className="text-sm font-medium text-muted-foreground mb-3">{t('devices.vehicleDetails')}</p>
              <div className="grid gap-4">
                <div className="grid grid-cols-2 gap-4">
                  {/* Make combobox */}
                  <div className="grid gap-2">
                    <Label>{t('devices.make')}</Label>
                    <Popover open={makeOpen} onOpenChange={setMakeOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={makeOpen}
                          className="justify-between font-normal"
                        >
                          <span className="truncate">
                            {formData.make || t('devices.makePlaceholder')}
                          </span>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                        <Command>
                          <CommandInput
                            placeholder={t('devices.makePlaceholder')}
                            value={makeSearch}
                            onValueChange={setMakeSearch}
                          />
                          <CommandList>
                            <CommandEmpty>
                              {makeSearch ? (
                                <button
                                  type="button"
                                  className="w-full text-sm cursor-pointer hover:underline"
                                  onClick={() => {
                                    setFormData({ ...formData, make: makeSearch, model: '' });
                                    setMakeSearch('');
                                    setMakeOpen(false);
                                  }}
                                >
                                  {t('devices.useCustomValue', { value: makeSearch })}
                                </button>
                              ) : (
                                t('devices.noResults')
                              )}
                            </CommandEmpty>
                            <CommandGroup>
                              {brandNames.map((brand) => (
                                <CommandItem
                                  key={brand}
                                  value={brand}
                                  onSelect={(val) => {
                                    const selected = VEHICLE_BRANDS.find(
                                      (b) => b.brand.toLowerCase() === val.toLowerCase(),
                                    );
                                    setFormData({
                                      ...formData,
                                      make: selected?.brand ?? val,
                                      model: '',
                                    });
                                    setMakeSearch('');
                                    setMakeOpen(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      'mr-2 h-4 w-4',
                                      formData.make.toLowerCase() === brand.toLowerCase()
                                        ? 'opacity-100'
                                        : 'opacity-0',
                                    )}
                                  />
                                  {brand}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  {/* Model combobox */}
                  <div className="grid gap-2">
                    <Label>{t('devices.model')}</Label>
                    <Popover open={modelOpen} onOpenChange={setModelOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={modelOpen}
                          className="justify-between font-normal"
                        >
                          <span className="truncate">
                            {formData.model || t('devices.modelPlaceholder')}
                          </span>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                        <Command>
                          <CommandInput
                            placeholder={t('devices.modelPlaceholder')}
                            value={modelSearch}
                            onValueChange={setModelSearch}
                          />
                          <CommandList>
                            <CommandEmpty>{t('devices.noResults')}</CommandEmpty>
                            {modelSearch && !modelsForBrand.some(m => m.toLowerCase() === modelSearch.toLowerCase()) && (
                              <CommandGroup>
                                <CommandItem
                                  value={`__custom__${modelSearch}`}
                                  onSelect={() => {
                                    setFormData({ ...formData, model: modelSearch });
                                    setModelSearch('');
                                    setModelOpen(false);
                                  }}
                                >
                                  {t('devices.useCustomValue', { value: modelSearch })}
                                </CommandItem>
                              </CommandGroup>
                            )}
                            <CommandGroup>
                              {modelsForBrand.map((model) => (
                                <CommandItem
                                  key={model}
                                  value={model}
                                  onSelect={(val) => {
                                    const selected = modelsForBrand.find(
                                      (m) => m.toLowerCase() === val.toLowerCase(),
                                    );
                                    setFormData({ ...formData, model: selected ?? val });
                                    setModelSearch('');
                                    setModelOpen(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      'mr-2 h-4 w-4',
                                      formData.model.toLowerCase() === model.toLowerCase()
                                        ? 'opacity-100'
                                        : 'opacity-0',
                                    )}
                                  />
                                  {model}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="edit-vehicle-year">{t('devices.year')}</Label>
                    <Input
                      id="edit-vehicle-year"
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                      placeholder={t('devices.yearPlaceholder')}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="edit-vehicle-model-year">{t('devices.modelYear')}</Label>
                    <Input
                      id="edit-vehicle-model-year"
                      value={formData.modelYear}
                      onChange={(e) => setFormData({ ...formData, modelYear: e.target.value })}
                      placeholder={t('devices.yearPlaceholder')}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="edit-vehicle-color">{t('devices.color')}</Label>
                    <Input
                      id="edit-vehicle-color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      placeholder={t('devices.colorPlaceholder')}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="edit-vehicle-speed-limit">{t('devices.speedLimit')}</Label>
                    <div className="relative">
                      <Input
                        id="edit-vehicle-speed-limit"
                        type="number"
                        min="0"
                        value={formData.speedLimit}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || Number(value) >= 0) setFormData({ ...formData, speedLimit: value });
                        }}
                        placeholder={t('devices.speedLimitPlaceholder')}
                        className="pr-12"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">km/h</span>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="edit-vehicle-plate">{t('devices.licensePlate')}</Label>
                    <Input
                      id="edit-vehicle-plate"
                      value={formData.licensePlate}
                      onChange={(e) => setFormData({ ...formData, licensePlate: e.target.value })}
                      placeholder={t('devices.licensePlatePlaceholder')}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="edit-vehicle-vin">{t('devices.vin')}</Label>
                    <Input
                      id="edit-vehicle-vin"
                      value={formData.vin}
                      onChange={(e) => setFormData({ ...formData, vin: e.target.value })}
                      placeholder={t('devices.vinPlaceholder')}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="edit-vehicle-renavan">{t('devices.renavan')}</Label>
                    <Input
                      id="edit-vehicle-renavan"
                      value={formData.renavan}
                      onChange={(e) => setFormData({ ...formData, renavan: e.target.value })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="edit-vehicle-activation-date">{t('devices.activationDate')}</Label>
                    <Input
                      id="edit-vehicle-activation-date"
                      type="date"
                      value={formData.activationDate}
                      onChange={(e) => setFormData({ ...formData, activationDate: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit-vehicle-notes">{t('common.notes')}</Label>
                  <Textarea id="edit-vehicle-notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} />
                </div>
                <div className="grid gap-2">
                  <Label>{t('devices.deviceStatus')}</Label>
                  <Select
                    value={formData.disabled ? 'disabled' : 'enabled'}
                    onValueChange={(v) => setFormData({ ...formData, disabled: v === 'disabled' })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="enabled">{t('devices.statusEnabled')}</SelectItem>
                      <SelectItem value="disabled">{t('devices.statusDisabled')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="edit-odometer">{t('devices.odometer')}</Label>
                    <div className="relative">
                      <Input
                        id="edit-odometer"
                        type="number"
                        min="0"
                        value={formData.odometerKm}
                        onChange={(e) => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) setFormData({ ...formData, odometerKm: v }); }}
                        placeholder={currentOdometerKm ?? t('devices.odometerPlaceholder')}
                        className="pr-10"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">km</span>
                    </div>
                    {currentOdometerKm && (
                      <p className="text-xs text-muted-foreground">{t('devices.current')}: {Number(currentOdometerKm).toLocaleString()} km</p>
                    )}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="edit-engine-hours">{t('devices.engineHours')}</Label>
                    <div className="relative">
                      <Input
                        id="edit-engine-hours"
                        type="number"
                        min="0"
                        value={formData.engineHours}
                        onChange={(e) => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) setFormData({ ...formData, engineHours: v }); }}
                        placeholder={currentEngineHours ?? t('devices.engineHoursPlaceholder')}
                        className="pr-10"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">h</span>
                    </div>
                    {currentEngineHours && (
                      <p className="text-xs text-muted-foreground">{t('devices.current')}: {currentEngineHours} h</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={updateMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              {t('common.update')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>}
    </Dialog>
  );
}
